import React, { useState, useEffect } from "react";
import {
  FaCalendarAlt,
  FaTachometerAlt,
  FaSignOutAlt,
  FaCog,
  FaBars,
  FaTimes,
  FaFileAlt,
  FaUsers,
  FaArrowLeft,
  FaCashRegister,
  FaBox
} from "react-icons/fa";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import DashboardTab from "../components/tabs/DashboardTab";
import EventsTab from "../components/tabs/EventsTab";
import SettingsTab from "../components/tabs/SettingsTab";
import ReportsTab from "../components/tabs/ReportsTab";
// import POSTab from "../components/tabs/POSTab";
import ProductsTab from "../components/tabs/ProductsTab";

function VendorDashboard() {
  const { vendorId } = useParams();
  const { organizationId } = useParams();
  const location = useLocation();
  
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'dashboard';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const isFromOrganizationDashboard = location.pathname.includes('organization');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/vendor-login");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSignOut = async () => {
    try {
      const sessionStr = localStorage.getItem('session');
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        
        const response = await fetch('/api/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          console.error('Error signing out:', response.statusText);
        }
      }
      
      localStorage.removeItem('session');
      
      navigate("/vendor-login");
      window.location.reload();
    } catch (error) {
      console.error("Error signing out:", error.message);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardTab vendorId={vendorId} />;
      case "events":
        return <EventsTab vendorId={vendorId} />;
      case "reports":
        return <ReportsTab vendorId={vendorId} />;
      case "settings":
        return <SettingsTab vendorId={vendorId} />;
      // case "pos":
      //   return <POSTab vendorId={vendorId} />;
      case "products":
        return <ProductsTab vendorId={vendorId} />;
      default:
        return null;
    }
  };

  const NavItem = ({ icon: Icon, label, tabName }) => (
    <button
      onClick={() => {
        setActiveTab(tabName);
        setIsSidebarOpen(false);
      }}
      className={`flex items-center w-full px-6 py-4 text-left transition-colors duration-200 ${
        activeTab === tabName
          ? "bg-blue-600 text-white"
          : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      <Icon
        className={`w-5 h-5 mr-3 ${
          activeTab === tabName ? "text-white" : "text-gray-500"
        }`}
      />
      <span className="font-medium">{label}</span>
    </button>
  );

  return (
    <div className="flex h-screen bg-gray-100">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div
        className={`fixed lg:static inset-y-0 left-0 w-64 bg-white shadow-lg transform ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 transition-transform duration-200 ease-in-out z-50`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b flex justify-center">
            <h1 className="text-2xl font-bold text-center text-gray-800">
              Valley Tickets
            </h1>
          </div>

          <nav className="flex-1 py-4">
            {isFromOrganizationDashboard && (
              <button
                onClick={() => navigate(`/organization/${organizationId}`)}
                className="flex items-center px-6 py-4 text-red-600 hover:bg-red-50 transition-colors duration-200"
              >
                <FaArrowLeft className="w-5 h-5 mr-3" />
                <span className="font-medium">Back to Organization</span>
              </button>
            )}
            <NavItem
              icon={FaTachometerAlt}
              label="Dashboard"
              tabName="dashboard"
            />
            <NavItem icon={FaCalendarAlt} label="Events" tabName="events" />
            {/* <NavItem icon={FaCashRegister} label="POS" tabName="pos" /> */}
            {/* <NavItem icon={FaBox} label="Products" tabName="products" /> */}
            <NavItem icon={FaFileAlt} label="Reports" tabName="reports" />
            <NavItem icon={FaCog} label="Settings" tabName="settings" />
          </nav>

          <button
            onClick={handleSignOut}
            className="flex items-center px-6 py-4 text-red-600 hover:bg-red-50 transition-colors duration-200"
          >
            <FaSignOutAlt className="w-5 h-5 mr-3" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
                aria-label="Toggle Sidebar"
              >
                {isSidebarOpen ? (
                  <FaTimes className="w-6 h-6 text-gray-600" />
                ) : (
                  <FaBars className="w-6 h-6 text-gray-600" />
                )}
              </button>
              <h1 className="ml-4 text-xl font-semibold text-gray-800">
                {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
              </h1>
            </div>
          </div>
        </header>

        
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default VendorDashboard;
